import React, {useEffect, useRef, useState} from 'react';
import ChartJS from 'chart.js/auto';
import {moment, get, map, first, last, secondsToTime, padStart, identity, maxBy} from 'helpers';
import 'chartjs-adapter-moment';

export function Chart({barPercentage = 1.1, ...props}) {
  const selected = useRef();
  const canvas = useRef();
  const chart = useRef();
  const [ colors ] = useState({});

  selected.current = props.selected;

  //create
  useEffect(() => {
    const root = window.document.body.querySelector('.root');
    colors.primary = getComputedStyle(root).getPropertyValue('--primary');
    colors.orange = getComputedStyle(root).getPropertyValue('--orange');
    colors.green = getComputedStyle(root).getPropertyValue('--green');
    colors.danger = getComputedStyle(root).getPropertyValue('--danger');
    colors.default = getComputedStyle(canvas.current).color;
    colors.blue = '#5E9AC8';

    ChartJS.defaults.plugins.legend.display = false;
    ChartJS.defaults.color = colors.default;
    ChartJS.defaults.plugins.tooltip.enabled = false;

    chart.current = new ChartJS(canvas.current.getContext('2d'), {
      type: 'bar',
      data: {
        datasets: [{
          data: [],
          barPercentage: barPercentage,
          pointRadius: 0,
          categoryPercentage: 1.0,
          fill: { target: 'origin' },
        }],
      },
      options: {
        animation: { duration: 0 },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'day'
            },
            ticks: {
              maxRotation: 0,
              color: ({ tick }) => tick.label == selected.current ? colors.primary : ChartJS.defaults.color,
              callback: (label, index, array) => array.length > 7 ? label : moment.unix(array[index].value/1000).calendar(),
            },
            grid: {
              drawTicks: false,
              lineWidth: 0,
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              callback: props.isTime ? formatDuration : identity,
            },
            grid: {
              tickColor: 'transparent',
            }
          }
        }
      }
    });
  }, []);

  useEffect(() => {
    const context = canvas.current.getContext('2d');
    const background = context.createLinearGradient(0,0,0,100);
    background.addColorStop(0, colors[props.color || 'green']);
    background.addColorStop(1, colors.blue);

    if (props.period) {
      chart.current.config.options.scales.x.suggestedMin = first(props.period);
      chart.current.config.options.scales.x.suggestedMax = last(props.period);
    }

    chart.current.config.type = get({ discrete: 'bar', cumulative: 'line' }, props.type) || props.type;
    chart.current.data.labels = map(props.data, 'moment');
    chart.current.data.datasets[0].data = map(props.data, 'value');
    chart.current.data.datasets[0].backgroundColor =
    chart.current.data.datasets[0].fill.above = background;
    chart.current.update();

    requestAnimationFrame(() => chart.current.options.animation.duration = 250);

  }, [ props.type, props.data, props.total, props.selected ]);

  useEffect(() => {
    if (props.onClick) {
      ChartJS.defaults.onClick = event => {
        props.onClick(event, chart.current.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true));
      }
    }
  }, [ props.onClick ]);

  return (
    <canvas className={props.className} ref={canvas} height={props.height || 80} />
  );
}

function formatDuration(value, _, array) {
  if (maxBy(array, 'value').value > 1) {
    let { hours, minutes } = secondsToTime(value);
    if (hours) {
      minutes = padStart(minutes, 2, 0);
    }
    return [ hours > 0 && hours + 'h', minutes + 'm'].filter(Boolean).join(' ');
  }
  return '';
}
